import { ExpandMore } from "@mui/icons-material";
import { Avatar, Box, Dropdown, Menu, MenuButton, MenuItem, Tooltip, Typography } from "@mui/joy";
import { NavigateOptions, useMatchRoute, useRouter } from "@tanstack/react-router";
import { ApiVenueDetail, ClimbType } from "griptonite-api-client";
import { memo, useCallback, useMemo } from "react";

import type { SwitchVenueLinkOpts } from "@/routing/__root";

import { useCurrentRouteMatch } from "@/hooks/useCurrentRouteMatch";
import {
  useAvailableVenuesQuery,
  useCurrentClimbType,
  useCurrentVenueQuery,
  useCurrentVenueUuid,
} from "@/hooks/venues";
import getThumbnailUrl from "@/utils/getThumbnailUrl";

import { CollapseText, useSidebarExpanded } from "./SidebarExpanded";

const VenueSwitcher = () => {
  const { data: currentVenue } = useCurrentVenueQuery();
  const isExpanded = useSidebarExpanded();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: 1,
        paddingX: 1.5,
        paddingY: 1,
      }}
    >
      <Dropdown>
        <Tooltip
          title={
            <Typography level="title-md" textColor="white" fontWeight="bold">
              {currentVenue?.name}
            </Typography>
          }
          size="lg"
          disableHoverListener={isExpanded}
          disableFocusListener={isExpanded}
          disableTouchListener={isExpanded}
        >
          <MenuButton
            slots={{ root: Box }}
            slotProps={{
              root: {
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: 1,
              },
            }}
          >
            {currentVenue ? (
              <>
                <Avatar
                  alt={currentVenue.name}
                  src={currentVenue.logo ? getThumbnailUrl(currentVenue.logo.url) : undefined}
                  sx={{ borderRadius: 4, width: 36, height: 36 }}
                  variant="outlined"
                />

                <CollapseText>
                  <Typography
                    sx={{
                      fontSize: "16px",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {currentVenue.name}
                  </Typography>
                </CollapseText>
              </>
            ) : (
              <>
                <Avatar sx={{ borderRadius: 4, width: 36, height: 36 }} variant="outlined" />
              </>
            )}
            <ExpandMore />
          </MenuButton>
        </Tooltip>
        <Menu sx={{ maxHeight: "calc(100dvh - 100px)" }}>
          <VenueSwitcherMenuItems />
        </Menu>
      </Dropdown>
    </Box>
  );
};

/** Lists the other venues the user can switch to, each linking to the same page at that venue. */
const VenueSwitcherMenuItems = () => {
  const { data: availableVenues } = useAvailableVenuesQuery();
  const currentVenueUuid = useCurrentVenueUuid();
  const venues = useMemo(
    () => availableVenues?.filter((venue) => venue.uuid !== currentVenueUuid) ?? [],
    [availableVenues, currentVenueUuid],
  );
  const { hrefs, switchTo } = useVenueSwitchLinks(venues);

  return (
    <>
      {venues.map((venue) => (
        <VenueMenuItem
          key={venue.uuid}
          venue={venue}
          href={hrefs.get(venue.uuid)!}
          onSelect={switchTo}
        />
      ))}
    </>
  );
};

type SwitchVenueDestination = {
  to: string;
  params?: Record<string, unknown>;
  search?: Record<string, unknown>;
};

/**
 * Where switching venue takes the user. On a venue page that's the same page at
 * the new venue, unless the route names somewhere else in `switchVenueLinkOpts`.
 * Anywhere else it's the new venue's routes page.
 */
const useSwitchVenueDestination = (): SwitchVenueDestination => {
  const matchRoute = useMatchRoute();
  const isVenueRoute = !!matchRoute({ to: "/gym/$venueUuid", fuzzy: true });

  const currentRoute = useCurrentRouteMatch();
  const currentPath = currentRoute?.fullPath;
  const currentSearch = currentRoute?.search;
  // Routes set this from beforeLoad, so the router infers each route's literal
  // shape; read it back through the declared type.
  const switchVenueLinkOpts: SwitchVenueLinkOpts | undefined =
    currentRoute?.context?.switchVenueLinkOpts;

  return useMemo(() => {
    if (!isVenueRoute) {
      return { to: "/gym/$venueUuid/routes/$climbType", search: {} };
    }
    if (switchVenueLinkOpts) {
      return {
        to: switchVenueLinkOpts.path,
        params: switchVenueLinkOpts.params,
        search: switchVenueLinkOpts.search,
      };
    }
    return { to: currentPath, search: currentSearch };
  }, [isVenueRoute, switchVenueLinkOpts, currentPath, currentSearch]);
};

/** Keeps the current climb type if the venue has it, and otherwise uses the venue's first. */
const climbTypeAtVenue = (venue: ApiVenueDetail, currentClimbType: ClimbType | undefined) =>
  currentClimbType && venue.climbTypes.includes(currentClimbType)
    ? currentClimbType
    : venue.climbTypes[0];

const PLACEHOLDER_VENUE_UUID = "00000000-0000-4000-8000-000000000000";

/**
 * Returns each venue's href, and a function that switches to a venue.
 *
 * Building a router location for each of ~200 venues on every render is slow.
 * The hrefs only differ by venue UUID and climb type, though, so we build one
 * per climb type with a placeholder UUID, then swap in each venue's UUID.
 */
const useVenueSwitchLinks = (venues: ApiVenueDetail[]) => {
  const router = useRouter();
  const destination = useSwitchVenueDestination();
  const currentClimbType = useCurrentClimbType() as ClimbType | undefined;

  const linkOptions = useCallback(
    (venueUuid: string, climbType: ClimbType | undefined) =>
      ({
        to: destination.to,
        params: destination.params ?? { venueUuid, climbType },
        search: destination.search,
      }) as NavigateOptions,
    [destination],
  );

  const hrefs = useMemo(() => {
    const hrefTemplates = new Map<ClimbType | undefined, string>();
    const hrefs = new Map<string, string>();
    for (const venue of venues) {
      const climbType = climbTypeAtVenue(venue, currentClimbType);
      if (!hrefTemplates.has(climbType)) {
        const location = router.buildLocation(linkOptions(PLACEHOLDER_VENUE_UUID, climbType));
        hrefTemplates.set(climbType, router.history.createHref(location.publicHref));
      }
      const href = hrefTemplates.get(climbType)!.replace(PLACEHOLDER_VENUE_UUID, venue.uuid);
      hrefs.set(venue.uuid, href);
    }
    return hrefs;
  }, [venues, currentClimbType, linkOptions, router]);

  const switchTo = useCallback(
    (venue: ApiVenueDetail) => {
      router.navigate(linkOptions(venue.uuid, climbTypeAtVenue(venue, currentClimbType)));
    },
    [router, linkOptions, currentClimbType],
  );

  return { hrefs, switchTo };
};

const isPlainLeftClick = (e: React.MouseEvent) =>
  !e.defaultPrevented && e.button === 0 && !e.metaKey && !e.altKey && !e.ctrlKey && !e.shiftKey;

const VenueMenuItem = memo(function VenueMenuItem({
  venue,
  href,
  onSelect,
}: {
  venue: ApiVenueDetail;
  href: string;
  onSelect: (venue: ApiVenueDetail) => void;
}) {
  return (
    <MenuItem
      component="a"
      href={href}
      onClick={(e) => {
        // Leave modified clicks to the browser, so cmd-click opens a new tab.
        if (!isPlainLeftClick(e)) {
          return;
        }
        e.preventDefault();
        onSelect(venue);
      }}
    >
      <Avatar
        src={getThumbnailUrl(venue.logo?.url) ?? undefined}
        alt={venue.name}
        sx={{
          width: 24,
          height: 24,
          borderRadius: 4,
        }}
      />

      {venue.name}
    </MenuItem>
  );
});

export default VenueSwitcher;
